import {
  CometChatCalls,
  type SessionSettings,
} from '@cometchat/calls-sdk-javascript';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './JoinSession.css';
import cometChatLogoDark from './assets/cometchat_logo_dark.svg';
import { Avatar, Icon } from './components';
import ConfigOverlay from './components/ConfigOverlay/ConfigOverlay';
import { auth } from './firebase';
import { getRandomMeetingId } from './utils/common';
interface JoinSessionProps {
  sessionId: string;
  initialMeetingName?: string;
  onSessionIdChange: (sessionId: string) => void;
  onStartSession: (
    sessionSettings: SessionSettings & { callToken: string | null },
  ) => void;
  onLogout: () => void;
}

const JoinSession: React.FC<JoinSessionProps> = ({
  sessionId,
  initialMeetingName,
  onSessionIdChange,
  onStartSession,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [hasVideoPermission, setHasVideoPermission] = useState(false);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [meetingName, setMeetingName] = useState(initialMeetingName || '');
  const [meetingNameInputDisabled, setMeetingNameInputDisabled] =
    useState(false);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState<string>('');
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('');
  const [selectedVideoInput, setSelectedVideoInput] = useState<string>('');
  const [showAudioInputDropdown, setShowAudioInputDropdown] = useState(false);
  const [showAudioOutputDropdown, setShowAudioOutputDropdown] = useState(false);
  const [showVideoInputDropdown, setShowVideoInputDropdown] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const audioInputDropdownRef = useRef<HTMLDivElement>(null);
  const audioOutputDropdownRef = useRef<HTMLDivElement>(null);
  const videoInputDropdownRef = useRef<HTMLDivElement>(null);
  const callTokenRef = useRef<string | null>(null);
  const isCometChatEmail = auth.currentUser?.email?.endsWith('@cometchat.com');

  // Function to set video source and play
  const setVideoSource = useCallback(async (stream: MediaStream | null) => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, []);

  useEffect(() => {
    if (showVideoPreview) {
      CometChatCalls.generateToken(sessionId).then(({ token }) => {
        callTokenRef.current = token;
        (CometChatCalls as any).connectToRoom(token);
      });
    }
  }, [showVideoPreview]);

  // Function to enumerate and update devices
  const updateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
      const videoInputs = devices.filter(d => d.kind === 'videoinput');

      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);
      setVideoDevices(videoInputs);

      // Chrome uses 'default' deviceId, Firefox uses first device in list
      const defaultAudioInput =
        audioInputs.find(d => d.deviceId === 'default') || audioInputs[0];
      const defaultVideoInput =
        videoInputs.find(d => d.deviceId === 'default') || videoInputs[0];
      const defaultAudioOutput =
        audioOutputs.find(d => d.deviceId === 'default') || audioOutputs[0];

      // Update audio input selection
      if (defaultAudioInput) {
        setSelectedAudioInput(defaultAudioInput.deviceId);
      }

      // Update video input selection
      if (defaultVideoInput) {
        setSelectedVideoInput(defaultVideoInput.deviceId);
      }

      // Update audio output selection
      if (defaultAudioOutput) {
        setSelectedAudioOutput(defaultAudioOutput.deviceId);
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }, []);

  useEffect(() => {
    updateDevices();

    const handleDeviceChange = () => {
      updateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [updateDevices]);

  // Initialize camera preview only when showing video preview
  const initializeMedia = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
          deviceId: selectedVideoInput
            ? { exact: selectedVideoInput }
            : undefined,
        },
        audio: true,
      });

      stream.getTracks().forEach(track => {
        if (track.kind === 'audio') {
          track.enabled = false;
        }
      });

      streamRef.current = stream;
      setHasVideoPermission(true);
      setIsStreamReady(true);

      // Re-enumerate devices after permission granted (needed for Firefox)
      await updateDevices();

      await setVideoSource(stream);
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setHasVideoPermission(false);
      setIsStreamReady(false);
    }
  }, [selectedVideoInput, setVideoSource, updateDevices]);

  useEffect(() => {
    if (isVideoMuted) {
      if (streamRef.current) {
        streamRef.current.getVideoTracks().forEach(track => {
          track.stop();
        });
      }
    } else {
      if (!showVideoPreview) return;
      initializeMedia();
    }
  }, [initializeMedia, isVideoMuted, showVideoPreview]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Check for session ID and meeting name in query parameters and clean URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromQuery = urlParams.get('sessionId');
    const sessionIdFromPath =
      window.location.pathname.split('/').filter(Boolean)[0] ||
      sessionIdFromQuery;
    const meetingNameFromQuery = urlParams.get('meetingName');

    if (sessionIdFromPath) {
      onSessionIdChange(sessionIdFromPath);
    }

    if (meetingNameFromQuery) {
      setMeetingName(meetingNameFromQuery);
      setMeetingNameInputDisabled(true);
    }

    if (sessionIdFromPath) {
      setShowVideoPreview(true);
    }
  }, [onSessionIdChange]);

  // Handle video toggle
  const toggleVideo = useCallback(async () => {
    setIsVideoMuted(!isVideoMuted);
  }, [isVideoMuted]);

  // Handle mic toggle
  const toggleMic = useCallback(() => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !isMicMuted;
      });
    }
    setIsMicMuted(!isMicMuted);
  }, [isMicMuted]);

  // Effect to handle video element when state changes
  useEffect(() => {
    if (
      isStreamReady &&
      streamRef.current &&
      videoRef.current &&
      showVideoPreview
    ) {
      if (!isVideoMuted) {
        setVideoSource(streamRef.current);
      } else {
        setVideoSource(null);
      }
    }
  }, [isVideoMuted, isStreamReady, showVideoPreview, setVideoSource]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }

      // Close device dropdowns when clicking outside
      if (
        audioInputDropdownRef.current &&
        !audioInputDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAudioInputDropdown(false);
      }

      if (
        audioOutputDropdownRef.current &&
        !audioOutputDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAudioOutputDropdown(false);
      }

      if (
        videoInputDropdownRef.current &&
        !videoInputDropdownRef.current.contains(event.target as Node)
      ) {
        setShowVideoInputDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to get device name
  const getDeviceName = (
    devices: MediaDeviceInfo[],
    deviceId: string,
    defaultName: string,
  ) => {
    const device = devices.find(d => d.deviceId === deviceId);
    return device?.label || defaultName;
  };

  // Handle device selection
  const handleDeviceSelection = async (
    deviceType: 'audioInput' | 'audioOutput' | 'videoInput',
    deviceId: string,
  ) => {
    if (deviceType === 'audioInput') {
      setSelectedAudioInput(deviceId);
      setShowAudioInputDropdown(false);
    } else if (deviceType === 'audioOutput') {
      setSelectedAudioOutput(deviceId);
      setShowAudioOutputDropdown(false);
    } else if (deviceType === 'videoInput') {
      setSelectedVideoInput(deviceId);
      setShowVideoInputDropdown(false);
    }
  };

  const handleJoinMeeting = () => {
    setShowVideoPreview(true);
  };

  const handleStartInstantMeeting = () => {
    const sessionIdToJoin = getRandomMeetingId();
    onSessionIdChange(sessionIdToJoin);
    window.history.replaceState({}, document.title, `/${sessionIdToJoin}`);
    setShowVideoPreview(true);
  };

  const handleJoinCall = (companionMode = false) => {
    console.time('JoinedSessionIn');
    const finalMeetingName = meetingName.trim() || 'CometChat Meeting';
    onStartSession({
      startAudioMuted: isMicMuted,
      startVideoPaused: isVideoMuted,
      title: finalMeetingName,
      audioInputDeviceId: selectedAudioInput,
      audioOutputDeviceId: selectedAudioOutput,
      videoInputDeviceId: selectedVideoInput,
      callToken: callTokenRef.current,
      enableCompanionMode: companionMode,
    } as SessionSettings & { callToken: string | null });
  };

  // First screen: Session ID or Instant Meeting
  if (!showVideoPreview) {
    return (
      <div className="join-session-container">
        <div className="join-session-card-wrapper">
          <div className="cometchat-logo">
            <img src={cometChatLogoDark} alt="" />
          </div>
          <div
            className={clsx('join-session-card', {
              'join-session-card-initial': !showVideoPreview,
            })}>
            <div className="join-session-header">
              <div className="user-profile-dropdown" ref={dropdownRef}>
                <div
                  className="user-avatar-trigger"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}>
                  <Avatar
                    size={45}
                    name={CometChatCalls.getLoggedInUser()?.name || ''}
                    imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                  />
                </div>

                {showUserDropdown && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        <Avatar
                          size={32}
                          name={CometChatCalls.getLoggedInUser()?.name || ''}
                          imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                        />
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-user-name">
                          {CometChatCalls.getLoggedInUser()?.name ||
                            'Guest User'}
                        </div>
                      </div>
                    </div>
                    <div
                      className="dropdown-item"
                      onClick={() => {
                        onLogout();
                        setShowUserDropdown(false);
                      }}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                      </svg>
                      <span>Logout</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-version">V.5.0.1</div>
                  </div>
                )}
              </div>
            </div>

            <div className="initial-screen">
              <div className="form-section">
                <div className="input-group">
                  <label htmlFor="sessionId" className="input-label">
                    Enter Session Id
                  </label>
                  <input
                    id="sessionId"
                    type="text"
                    placeholder="Session ID"
                    value={sessionId}
                    onChange={e => onSessionIdChange(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="action-buttons">
                <button
                  onClick={handleJoinMeeting}
                  disabled={!sessionId.trim()}
                  className={clsx('primary-btn', {
                    active: !!sessionId.trim(),
                  })}>
                  Join Meeting
                </button>

                {!sessionId.trim() && <div className="or-divider" />}

                {!sessionId.trim() && (
                  <button
                    onClick={handleStartInstantMeeting}
                    className="secondary-btn">
                    Start Instant Meeting
                  </button>
                )}
              </div>
            </div>
          </div>
          {isCometChatEmail && (
            <button
              className="history-btn"
              onClick={() => navigate('/history')}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.9443 20.8499C9.70361 20.8499 7.76386 20.1159 6.12503 18.6479C4.48619 17.1797 3.52511 15.3549 3.24178 13.1734C3.20844 12.9632 3.26419 12.7743 3.40903 12.6067C3.55369 12.439 3.73986 12.3494 3.96753 12.3379C4.17803 12.3264 4.36319 12.3887 4.52303 12.5249C4.68269 12.6611 4.78386 12.8379 4.82653 13.0554C5.07836 14.8296 5.87111 16.3083 7.20478 17.4917C8.53861 18.675 10.1182 19.2667 11.9435 19.2667C13.9784 19.2667 15.7069 18.5512 17.1293 17.1204C18.5514 15.6897 19.2625 13.9564 19.2625 11.9204C19.2625 9.90957 18.5455 8.20899 17.1115 6.81865C15.6775 5.42832 13.9501 4.73315 11.9293 4.73315C10.8069 4.73315 9.75828 4.9894 8.78328 5.5019C7.80828 6.0144 6.96803 6.69707 6.26253 7.5499H8.08328C8.30828 7.5499 8.49719 7.62649 8.65003 7.77965C8.80286 7.93282 8.87928 8.11874 8.87928 8.3374C8.87928 8.5624 8.80286 8.75132 8.65003 8.90415C8.49719 9.05682 8.30828 9.13315 8.08328 9.13315H4.36678C4.14178 9.13315 3.95428 9.05682 3.80428 8.90415C3.65428 8.75132 3.57928 8.5624 3.57928 8.3374V4.63415C3.57928 4.41649 3.65428 4.23132 3.80428 4.07865C3.95428 3.92615 4.14178 3.8499 4.36678 3.8499C4.59178 3.8499 4.78061 3.92649 4.93328 4.07965C5.08611 4.23282 5.16253 4.41874 5.16253 4.6374V6.38315C6.00703 5.37215 7.01578 4.58049 8.18878 4.00815C9.36178 3.43599 10.6086 3.1499 11.9293 3.1499C13.1569 3.1499 14.3122 3.3799 15.395 3.8399C16.4779 4.2999 17.423 4.9284 18.2305 5.7254C19.038 6.52224 19.6758 7.45432 20.1438 8.52165C20.6118 9.58899 20.8458 10.7369 20.8458 11.9654C20.8458 13.1939 20.6118 14.3477 20.1438 15.4269C19.6758 16.5061 19.0389 17.4471 18.2333 18.2499C17.4278 19.0527 16.4855 19.6867 15.4065 20.1519C14.3275 20.6172 13.1734 20.8499 11.9443 20.8499ZM12.8293 11.6474L15.5493 14.3364C15.7109 14.4982 15.7918 14.6888 15.7918 14.9082C15.7918 15.1277 15.7112 15.3166 15.55 15.4749C15.3889 15.6332 15.2028 15.7124 14.9918 15.7124C14.7806 15.7124 14.594 15.6316 14.432 15.4699L11.48 12.5337C11.3989 12.4528 11.3395 12.3646 11.302 12.2689C11.2645 12.1731 11.2458 12.0709 11.2458 11.9624V7.82065C11.2458 7.60215 11.3208 7.41624 11.4708 7.2629C11.6208 7.10974 11.8083 7.03315 12.0333 7.03315C12.2583 7.03315 12.4472 7.10974 12.6 7.2629C12.7529 7.41624 12.8293 7.60215 12.8293 7.82065V11.6474Z"
                  fill="#858585"
                />
              </svg>
              <span>History</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Second screen: Video preview with meeting name
  return (
    <div className="join-session-container">
      <div className="join-session-card-wrapper">
        <div className="cometchat-logo">
          <img src={cometChatLogoDark} alt="" />
        </div>
        <div
          className={clsx('join-session-card', {
            'join-session-card-preview': showVideoPreview,
          })}>
          <div className="join-session-header">
            <div className="user-profile-dropdown" ref={dropdownRef}>
              <div
                className="user-avatar-trigger"
                onClick={() => setShowUserDropdown(!showUserDropdown)}>
                <Avatar
                  size={45}
                  name={CometChatCalls.getLoggedInUser()?.name || ''}
                  imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                />
              </div>

              {showUserDropdown && (
                <div className="user-dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      <Avatar
                        size={32}
                        name={CometChatCalls.getLoggedInUser()?.name || ''}
                        imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                      />
                    </div>
                    <div className="dropdown-user-info">
                      <div className="dropdown-user-name">
                        {CometChatCalls.getLoggedInUser()?.name || 'Guest User'}
                      </div>
                    </div>
                  </div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      onLogout();
                      setShowUserDropdown(false);
                    }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor">
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                    </svg>
                    <span>Logout</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-version">V.5.0.1</div>
                </div>
              )}
            </div>
          </div>

          {/* Video Preview */}
          <div className="video-preview-container">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="video-preview"
              style={{
                display: hasVideoPermission && !isVideoMuted ? 'block' : 'none',
              }}
            />

            {(!hasVideoPermission || isVideoMuted) && (
              <div className="video-placeholder">
                <div className="avatar-placeholder">
                  <Avatar
                    imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                    name={CometChatCalls.getLoggedInUser()?.name || ''}
                    size={60}
                  />
                </div>
              </div>
            )}

            {/* Media Controls */}
            <div className="media-controls">
              <button
                onClick={toggleMic}
                className={`control-btn ${isMicMuted ? 'muted' : 'active'}`}
                title={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}>
                <Icon
                  size={24}
                  name={isMicMuted ? 'mic-off-fill' : 'mic-fill'}
                  fill={
                    isMicMuted
                      ? 'var(--cometchat-calls-error-color)'
                      : 'var(--cometchat-calls-static-white)'
                  }
                />
              </button>

              <button
                onClick={toggleVideo}
                className={`control-btn ${isVideoMuted ? 'muted' : 'active'}`}
                title={isVideoMuted ? 'Turn on camera' : 'Turn off camera'}
                disabled={!hasVideoPermission}>
                <Icon
                  size={24}
                  name={isVideoMuted ? 'video-off-fill' : 'video-fill'}
                  fill={
                    isVideoMuted
                      ? 'var(--cometchat-calls-error-color)'
                      : 'var(--cometchat-calls-static-white)'
                  }
                />
              </button>
            </div>
          </div>

          <div className="device-selection-container">
            <div
              className="device-row"
              ref={audioInputDropdownRef}
              onClick={() => {
                setShowAudioInputDropdown(!showAudioInputDropdown);
              }}>
              <div className="device-control">
                <Icon
                  size={24}
                  name="mic-fill"
                  fill="var(--cometchat-calls-icon-color-primary)"
                />
                <button className="custom-device-select">
                  <span className="device-name">
                    {getDeviceName(
                      audioInputDevices,
                      selectedAudioInput,
                      'MacBook Air Microphone',
                    )}
                  </span>
                  <div
                    className="dropdown-arrow"
                    style={{
                      transform: showAudioInputDropdown
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      transition: 'transform 0.2s ease',
                    }}>
                    <Icon
                      size={24}
                      name="arrow-up"
                      fill="var(--cometchat-calls-icon-color-primary)"
                    />
                  </div>
                </button>

                {showAudioInputDropdown && (
                  <div className="custom-dropdown-menu">
                    {audioInputDevices.map(device => (
                      <div
                        key={device.deviceId}
                        className={`dropdown-option ${selectedAudioInput === device.deviceId ? 'selected' : 'not-selected'}`}
                        onClick={() =>
                          handleDeviceSelection('audioInput', device.deviceId)
                        }>
                        {selectedAudioInput === device.deviceId && (
                          <div className="dropdown-arrow">
                            <Icon
                              size={24}
                              name="check-fill"
                              fill="var(--cometchat-calls-static-white)"
                            />
                          </div>
                        )}
                        <span>
                          {device.label ||
                            `Microphone ${device.deviceId.slice(0, 8)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className="device-row"
              ref={audioOutputDropdownRef}
              onClick={() =>
                setShowAudioOutputDropdown(!showAudioOutputDropdown)
              }>
              <div className="device-control">
                <Icon
                  size={24}
                  name="wave"
                  fill="var(--cometchat-calls-icon-color-primary)"
                />
                <button className="custom-device-select">
                  <span className="device-name">
                    {getDeviceName(
                      audioOutputDevices,
                      selectedAudioOutput,
                      'MacBook Air Speakers',
                    )}
                  </span>
                  <div
                    className="dropdown-arrow"
                    style={{
                      transform: showAudioOutputDropdown
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      transition: 'transform 0.2s ease',
                    }}>
                    <Icon
                      size={24}
                      name="arrow-up"
                      fill="var(--cometchat-calls-icon-color-primary)"
                    />
                  </div>
                </button>

                {showAudioOutputDropdown && (
                  <div className="custom-dropdown-menu">
                    {audioOutputDevices.map(device => (
                      <div
                        key={device.deviceId}
                        className={`dropdown-option ${selectedAudioOutput === device.deviceId ? 'selected' : 'not-selected'}`}
                        onClick={() =>
                          handleDeviceSelection('audioOutput', device.deviceId)
                        }>
                        {selectedAudioOutput === device.deviceId && (
                          <div className="dropdown-arrow">
                            <Icon
                              size={24}
                              name="check-fill"
                              fill="var(--cometchat-calls-static-white)"
                            />
                          </div>
                        )}
                        <span>
                          {device.label ||
                            `Speaker ${device.deviceId.slice(0, 8)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className="device-row"
              ref={videoInputDropdownRef}
              onClick={() => {
                setShowVideoInputDropdown(!showVideoInputDropdown);
              }}>
              <div className="device-control">
                <Icon
                  size={24}
                  name="video-fill"
                  fill="var(--cometchat-calls-icon-color-primary)"
                />
                <button className="custom-device-select">
                  <span className="device-name">
                    {getDeviceName(
                      videoDevices,
                      selectedVideoInput,
                      'FaceTime HD Camera',
                    )}
                  </span>
                  <div
                    className="dropdown-arrow"
                    style={{
                      transform: showVideoInputDropdown
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      transition: 'transform 0.2s ease',
                    }}>
                    <Icon
                      size={24}
                      name="arrow-up"
                      fill="var(--cometchat-calls-icon-color-primary)"
                    />
                  </div>
                </button>

                {showVideoInputDropdown && (
                  <div className="custom-dropdown-menu">
                    {videoDevices.map(device => (
                      <div
                        key={device.deviceId}
                        className={`dropdown-option ${selectedVideoInput === device.deviceId ? 'selected' : 'not-selected'}`}
                        onClick={() =>
                          handleDeviceSelection('videoInput', device.deviceId)
                        }>
                        {selectedVideoInput === device.deviceId && (
                          <div className="dropdown-arrow">
                            <Icon
                              size={24}
                              name="check-fill"
                              fill="var(--cometchat-calls-static-white)"
                            />
                          </div>
                        )}
                        <span>
                          {device.label ||
                            `Camera ${device.deviceId.slice(0, 8)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Meeting Name Input */}
          <div className="form-section">
            <div className="input-group">
              <label htmlFor="meetingName" className="input-label">
                Meeting Name (Optional)
              </label>
              <input
                id="meetingName"
                type="text"
                placeholder="Enter meeting name"
                value={meetingName}
                onChange={e => setMeetingName(e.target.value)}
                className="form-input"
                disabled={meetingNameInputDisabled}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              onClick={() => handleJoinCall(true)}
              className="secondary-btn">
              <Icon size={20} name="companion" fill="currentColor" />
              Use Companion Mode
            </button>
            <button onClick={() => handleJoinCall()} className="primary-btn">
              Join Now
            </button>
          </div>
        </div>
      </div>
      <ConfigOverlay />
    </div>
  );
};

export default JoinSession;
